import { loadLanguage, loadRegions } from '../lib/onboardingStorage';
import { getSupabase, isSupabaseConfigured } from '../lib/supabase';
import { loadCloudAdsCache } from '../lib/cloudAdPool';
import { normalizeCityCode } from '../lib/cityCode';

const VN_CITY_CODES = new Set(['danang', 'hochiminh', 'hanoi', 'hoian', 'hue', 'DAD', 'SGN', 'HAN', 'HUI', 'HUE']);

const AD_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

let appUserIdCache = { id: null, at: 0 };
const APP_USER_CACHE_MS = 45_000;

const impressionOnce = new Set();

function resolveLanguage(ctxLang) {
  const fromCtx = typeof ctxLang === 'string' && ctxLang.trim();
  if (fromCtx) return fromCtx.slice(0, 2).toLowerCase();
  const ls = loadLanguage();
  if (ls && typeof ls === 'string') return ls.slice(0, 2).toLowerCase();
  return 'ko';
}

/** 최대 2개 지역: 컨텍스트(cityCode 우선) > 라벨 > 로컬스토리지 */
function resolveRegions(ctxRegions, ctxCityCodes) {
  if (Array.isArray(ctxCityCodes) && ctxCityCodes.length > 0) {
    return ctxCityCodes
      .map((c) => normalizeCityCode(c))
      .filter(Boolean)
      .slice(0, 2);
  }
  if (Array.isArray(ctxRegions) && ctxRegions.length > 0) {
    return ctxRegions.map((r) => regionTokenToCityCode(r)).filter(Boolean).slice(0, 2);
  }
  const ls = loadRegions();
  return Array.isArray(ls) ? ls.map((r) => regionTokenToCityCode(r)).filter(Boolean).slice(0, 2) : [];
}

function adLanguageMatches(adLang, userLang) {
  const a = String(adLang || '').toLowerCase().trim();
  if (!a || a === 'all') return true;
  return a === userLang;
}

function regionTokenToCityCode(token) {
  return normalizeCityCode(token);
}

/** region: global/vietnam/VN/cityCode 와 유저 cityCode 매칭 */
function adRegionMatches(adRegion, userCityCodes, countryCode) {
  const r = String(adRegion || '').trim().toLowerCase();
  if (r === 'global') return true;
  if (r === 'all') return true;
  if (!userCityCodes.length && !countryCode) return false;

  if (r === 'vietnam' || r === 'vn') {
    return String(countryCode || '').toUpperCase() === 'VN' || userCityCodes.some((code) => VN_CITY_CODES.has(code));
  }
  if (/^[a-z]{2}$/.test(r)) {
    return r.toUpperCase() === String(countryCode || '').toUpperCase();
  }
  const adCode = regionTokenToCityCode(adRegion);
  if (!adCode) return false;
  return userCityCodes.includes(adCode);
}

function adCategoryMatches(adCat, contextCategory) {
  if (!adCat || adCat === 'all') return true;
  if (!contextCategory || contextCategory === 'all') return true;
  return adCat === contextCategory;
}

function adPlacementMatches(adPlacement, ctxPlacement) {
  const a = String(adPlacement || 'all').trim();
  const c = String(ctxPlacement || 'all').trim();
  if (!c || c === 'all') return true;
  if (!a || a === 'all') return true;
  return a === c;
}

function adMediaMatches(adMediaKind, mediaPreference) {
  const a = String(adMediaKind || 'all').trim();
  const m = String(mediaPreference || 'all').trim();
  if (!m || m === 'all') return true;
  if (!a || a === 'all') return true;
  return a === m;
}

function isAdActiveNow(ad, now = new Date()) {
  if (!ad.active) return false;
  const start = ad.startDate ? new Date(ad.startDate) : null;
  const end = ad.endDate ? new Date(ad.endDate) : null;
  if (start && Number.isFinite(start.getTime()) && now < start) return false;
  if (end && Number.isFinite(end.getTime())) {
    const endDay = new Date(end);
    endDay.setHours(23, 59, 59, 999);
    if (now > endDay) return false;
  }
  return true;
}

/**
 * @param {{ language?: string, regions?: string[], type: 'feed'|'banner'|'category', category?: string }} ctx
 */
export function matchAds(ctx) {
  const { type, category = 'all', placement = 'all', mediaPreference = 'all' } = ctx;
  const language = resolveLanguage(ctx.language);
  const regions = resolveRegions(ctx.regions, ctx.cityCodes);
  const countryCode = String(ctx.countryCode || inferCountryCode(regions) || '')
    .toUpperCase()
    .trim();
  const userLang = language;
  // 예전엔 여기에 getAdminAdsList()(수정 불가능한 5개 고정 더미 광고)도 섞여 있었는데,
  // 실제 관리자가 편집할 수 있는 UI가 없어서 항상 같은 가짜 광고만 보여주고 있었음 — 제거함.
  const adsSource = [...loadCloudAdsCache()];

  const list = adsSource.filter(
    (ad) =>
      ad.type === type &&
      isAdActiveNow(ad) &&
      adLanguageMatches(ad.language, userLang) &&
      adRegionMatches(ad.region, regions, countryCode) &&
      adCategoryMatches(ad.category, category) &&
      adPlacementMatches(ad.placement, placement) &&
      adMediaMatches(ad.media_kind, mediaPreference),
  );

  function regionSpecificity(ad) {
    const r = String(ad.region || '').trim().toLowerCase();
    if (!r || r === 'global') return 0;
    if (r === 'vietnam' || r === 'vn') return 1;
    if (/^[a-z]{2}$/.test(r) && countryCode && r === countryCode.toLowerCase()) return 2;
    if (regionTokenToCityCode(r)) return 3;
    return 3;
  }

  function langBoost(ad) {
    const a = String(ad.language || '').toLowerCase().trim();
    if (!a || a === 'all') return 0;
    return a === userLang ? 1 : 0;
  }

  list.sort((a, b) => {
    const rs = regionSpecificity(b) - regionSpecificity(a);
    if (rs !== 0) return rs;
    const lb = langBoost(b) - langBoost(a);
    if (lb !== 0) return lb;
    const pr = (b.priority || 0) - (a.priority || 0);
    if (pr !== 0) return pr;
    return String(a.id).localeCompare(String(b.id));
  });
  return list;
}

export function pickAdForSlot(ctx, slotIndex) {
  const list = matchAds(ctx);
  if (!list.length) return null;
  return list[slotIndex % list.length];
}

/**
 * Feed placements tied to a specific hub slot (category !== `all`).
 * Used so life-hub shortcuts appear only when a dedicated placement exists (not wildcard).
 */
export function matchFeedAdsStrictCategory(ctx, slotCategory) {
  const cat = String(slotCategory || '').trim();
  if (!cat) return [];
  const base = matchAds({ ...ctx, type: 'feed', category: cat });
  return base.filter((ad) => {
    const ac = String(ad.category || '').trim();
    return ac && ac !== 'all' && ac === cat;
  });
}

export function hasDedicatedFeedAdForSlot(ctx, slotCategory) {
  return matchFeedAdsStrictCategory(ctx, slotCategory).length > 0;
}

export function resetAdStatsUserCache() {
  appUserIdCache = { id: null, at: 0 };
}

/** @param {object | null | undefined} ad */
export function parseAdUuidForStats(ad) {
  const raw = ad?.id;
  if (raw == null) return null;
  const s = String(raw);
  return AD_UUID_RE.test(s) ? s : null;
}

function inferCountryCode(regions) {
  const codes = Array.isArray(regions) ? regions.map((c) => normalizeCityCode(c)).filter(Boolean) : [];
  if (codes.some((c) => VN_CITY_CODES.has(c))) return 'VN';
  return 'VN';
}

async function resolveAppUserIdForStats(supabase) {
  const now = Date.now();
  if (appUserIdCache.at > 0 && now - appUserIdCache.at < APP_USER_CACHE_MS) {
    return appUserIdCache.id;
  }
  let id = null;
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (user) {
      const { data } = await supabase.from('users').select('id').eq('auth_id', user.id).maybeSingle();
      id = data?.id ?? null;
    }
  } catch {
    id = null;
  }
  appUserIdCache = { id, at: now };
  return id;
}

function impressionDedupeKey(ad) {
  return `${parseAdUuidForStats(ad) || 'local'}:${String(ad?.title || '')}:${String(ad?.link || '')}`;
}

async function insertAdStatRow(eventType, ad, ctx) {
  if (!isSupabaseConfigured) return;
  const supabase = getSupabase();
  if (!supabase) return;
  const language = resolveLanguage(ctx?.language);
  const regions = resolveRegions(ctx?.regions, ctx?.cityCodes);
  const country_code = inferCountryCode(regions);
  const ad_id = parseAdUuidForStats(ad);
  const user_id = await resolveAppUserIdForStats(supabase);
  const { error } = await supabase.from('ad_stats').insert({
    ad_id,
    event_type: eventType,
    user_id,
    country_code,
    language,
  });
  if (error && process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.warn('[ad_stats]', error.message);
  }
}

/** 광고 카드/배너가 화면에 올 때 호출 (동일 탭·동일 광고 1회) */
export function recordAdImpression(ad, ctx) {
  if (!ad) return;
  const k = impressionDedupeKey(ad);
  if (impressionOnce.has(k)) return;
  impressionOnce.add(k);
  void insertAdStatRow('impression', ad, ctx);
}

/** 광고 클릭 시 호출 */
export function recordAdClick(ad, ctx) {
  if (!ad) return;
  void insertAdStatRow('click', ad, ctx);
}
